import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Tracks writes that are in flight, so the application can show one standard
 * progress signal for every create, update and delete.
 *
 * The interceptor drives this, not feature components: 69 components performed
 * writes with no busy flag of their own, and a per-component flag would have to be
 * added -- correctly, including every error path -- to each of them. Counting at
 * the single HTTP boundary covers every write in the app, including ones written
 * after this.
 *
 * A counter rather than a boolean: two saves can overlap, and the signal must stay
 * up until the last one settles.
 */
@Injectable({ providedIn: 'root' })
export class BusyService {
  private readonly pending = new BehaviorSubject<number>(0);

  /** True while at least one write is in flight. */
  readonly busy$: Observable<boolean> = this.pending.pipe(
    map(count => count > 0),
    distinctUntilChanged()
  );

  start(): void {
    this.pending.next(this.pending.value + 1);
  }

  stop(): void {
    // Guard against a double `finalize` driving the count negative, which would
    // latch the indicator on for the rest of the session.
    this.pending.next(Math.max(0, this.pending.value - 1));
  }
}
